/**
 * RosterSecurityFormatView - Legacy-style roster display
 * 
 * P1 Feature: Security Format View with:
 * - Day columns showing shift count, assigned hours
 * - Shift cards with guard name, times, duration
 * - Clone Today, Assign, Split Shift, Bulk Shift buttons
 * - Daily/Weekly totals
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Copy, 
  UserPlus, 
  Scissors, 
  Layers,
  Pencil,
  Trash2,
  Loader2,
  Clock,
  ChevronDown
} from 'lucide-react';
import { formatTimeLocal, calculateHours, formatHoursDecimal } from '../lib/timecalc';
import { formatDateKey } from '../lib/timeformat';

// Status color indicators (0|0|2|0 dots)
const STATUS_COLORS = {
  unfilled: '#f97316',    // Orange
  pending: '#3b82f6',     // Blue  
  accepted: '#22c55e',    // Green
  declined: '#ef4444',    // Red
};

// Shift card background colors based on status
const CARD_COLORS = {
  accepted: 'bg-green-500/90',
  pending_acceptance: 'bg-green-400/80',
  declined: 'bg-red-400/80',
  scheduled: 'bg-green-300/70',
  unaccepted: 'bg-orange-400/80',
};

export default function RosterSecurityFormatView({
  days,
  siteId,
  rosterData,
  onCloneDay,
  onAddShift,
  onEditShift,
  onDeleteShift,
  onCommitDay,
  isAdmin,
  loading,
  api
}) {
  const [showCloneDayDialog, setShowCloneDayDialog] = useState(false);
  const [sourceDay, setSourceDay] = useState(null);
  const [targetDays, setTargetDays] = useState({});
  const [cloneMode, setCloneMode] = useState('shifts_only');
  const [cloneLoading, setCloneLoading] = useState(false);
  
  // P1: Bulk Shift state
  const [showBulkShiftDialog, setShowBulkShiftDialog] = useState(false);
  const [bulkTargetDays, setBulkTargetDays] = useState({});
  const [bulkShiftData, setBulkShiftData] = useState({
    start_time: '06:00',
    end_time: '18:00',
    break_minutes: 0,
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // P1: Split Shift state
  const [showSplitShiftDialog, setShowSplitShiftDialog] = useState(false);
  const [shiftToSplit, setShiftToSplit] = useState(null);
  const [splitTime, setSplitTime] = useState('12:00');
  const [splitLoading, setSplitLoading] = useState(false);

  // Calculate weekly totals
  const weeklyTotals = React.useMemo(() => {
    let totalShifts = 0;
    let totalHours = 0;
    let assignedHours = 0;
    let unassignedHours = 0;

    days.forEach(([_, day]) => {
      day.shifts?.forEach(shift => {
        totalShifts++;
        const hours = calculateHours(shift.start_time, shift.end_time, shift.break_minutes || 0);
        totalHours += hours;
        if (shift.officer_id) {
          assignedHours += hours;
        } else {
          unassignedHours += hours;
        }
      });
    });

    return { totalShifts, totalHours, assignedHours, unassignedHours };
  }, [days]);

  // Handle Clone Day
  const handleCloneDay = (day, dateKey) => {
    setSourceDay({ day, dateKey });
    // Reset target selection
    const initialTargets = {};
    days.forEach(([key, _]) => {
      if (key !== dateKey) {
        initialTargets[key] = false;
      }
    });
    setTargetDays(initialTargets);
    setShowCloneDayDialog(true);
  };

  const executeCloneDay = async () => {
    if (!sourceDay) return;
    
    const selectedTargets = Object.entries(targetDays)
      .filter(([_, selected]) => selected)
      .map(([dateKey, _]) => dateKey);

    if (selectedTargets.length === 0) {
      toast.error('Please select at least one target day');
      return;
    }

    setCloneLoading(true);
    try {
      await api.post(`/roster/v2/clone-day?site_id=${siteId}`, {
        source_date: sourceDay.dateKey,
        target_dates: selectedTargets,
        mode: cloneMode,
        overwrite_target: false,
        include_notes: true
      });
      
      toast.success(`Cloned ${sourceDay.dateKey} to ${selectedTargets.length} day(s)`);
      setShowCloneDayDialog(false);
      if (onCloneDay) onCloneDay();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone day');
    } finally {
      setCloneLoading(false);
    }
  };

  // P1: Handle Bulk Shift
  const openBulkShift = (day, dateKey) => {
    // Initialize with this day selected
    const initialTargets = {};
    days.forEach(([key, _]) => {
      initialTargets[key] = key === dateKey;
    });
    setBulkTargetDays(initialTargets);
    setShowBulkShiftDialog(true);
  };

  const executeBulkShift = async () => {
    const selectedDates = Object.entries(bulkTargetDays)
      .filter(([_, selected]) => selected)
      .map(([dateKey, _]) => dateKey);

    if (selectedDates.length === 0) {
      toast.error('Please select at least one day');
      return;
    }

    setBulkLoading(true);
    try {
      // Use clone-day endpoint with a temporary source
      // First create the shift on the first selected day
      const firstDate = selectedDates[0];
      
      // Create shift on first day
      const { localInputsToUTC } = await import('../lib/timecalc');
      const startTimeUTC = localInputsToUTC(firstDate, bulkShiftData.start_time);
      let endTimeUTC = localInputsToUTC(firstDate, bulkShiftData.end_time);
      
      // Handle overnight
      if (bulkShiftData.end_time <= bulkShiftData.start_time) {
        const [year, month, day] = firstDate.split('-').map(Number);
        const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
        const nextDayStr = nextDay.toISOString().split('T')[0];
        endTimeUTC = localInputsToUTC(nextDayStr, bulkShiftData.end_time);
      }
      
      // Create first shift
      await api.post('/roster/shifts', {
        site_id: siteId,
        start_time: startTimeUTC,
        end_time: endTimeUTC,
        break_minutes: bulkShiftData.break_minutes || 0,
      });
      
      // Clone to remaining days
      if (selectedDates.length > 1) {
        await api.post(`/roster/v2/clone-day?site_id=${siteId}`, {
          source_date: firstDate,
          target_dates: selectedDates.slice(1),
          mode: 'shifts_only',
          overwrite_target: false,
          include_notes: false
        });
      }
      
      toast.success(`Created shift on ${selectedDates.length} day(s)`);
      setShowBulkShiftDialog(false);
      if (onCloneDay) onCloneDay();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bulk shifts');
    } finally {
      setBulkLoading(false);
    }
  };

  // P1: Handle Split Shift
  const openSplitShift = (shift) => {
    setShiftToSplit(shift);
    // Default split time to middle of shift
    const startMinutes = timeToMinutes(formatTimeLocal(shift.start_time));
    const endMinutes = timeToMinutes(formatTimeLocal(shift.end_time));
    let midMinutes = Math.floor((startMinutes + endMinutes) / 2);
    if (endMinutes < startMinutes) midMinutes = Math.floor((startMinutes + endMinutes + 1440) / 2) % 1440;
    setSplitTime(minutesToTime(midMinutes));
    setShowSplitShiftDialog(true);
  };

  const executeSplitShift = async () => {
    if (!shiftToSplit) return;
    
    setSplitLoading(true);
    try {
      const { localInputsToUTC } = await import('../lib/timecalc');
      
      // Get the date from the original shift
      const shiftDate = new Date(shiftToSplit.start_time).toISOString().split('T')[0];
      const originalStart = formatTimeLocal(shiftToSplit.start_time);
      const originalEnd = formatTimeLocal(shiftToSplit.end_time);
      
      // Create first half: original start to split time
      const startTime1 = localInputsToUTC(shiftDate, originalStart);
      const endTime1 = localInputsToUTC(shiftDate, splitTime);
      
      // Create second half: split time to original end  
      const startTime2 = localInputsToUTC(shiftDate, splitTime);
      let endTime2 = localInputsToUTC(shiftDate, originalEnd);
      
      // Handle overnight for second half
      if (originalEnd <= splitTime) {
        const [year, month, day] = shiftDate.split('-').map(Number);
        const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
        const nextDayStr = nextDay.toISOString().split('T')[0];
        endTime2 = localInputsToUTC(nextDayStr, originalEnd);
      }
      
      // Delete original shift
      await api.delete(`/roster/shifts/${shiftToSplit.id}`);
      
      // Create two new shifts
      await api.post('/roster/shifts', {
        site_id: siteId,
        officer_id: shiftToSplit.officer_id,
        start_time: startTime1,
        end_time: endTime1,
        notes: shiftToSplit.notes ? `${shiftToSplit.notes} (Part 1)` : 'Part 1 of split',
        break_minutes: 0,
      });
      
      await api.post('/roster/shifts', {
        site_id: siteId,
        officer_id: shiftToSplit.officer_id,
        start_time: startTime2,
        end_time: endTime2,
        notes: shiftToSplit.notes ? `${shiftToSplit.notes} (Part 2)` : 'Part 2 of split',
        break_minutes: 0,
      });
      
      toast.success('Shift split successfully');
      setShowSplitShiftDialog(false);
      if (onCloneDay) onCloneDay();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to split shift');
    } finally {
      setSplitLoading(false);
    }
  };

  // Helper functions
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const minutesToTime = (minutes) => {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4" data-testid="security-format-view">
      {/* Weekly Summary Bar */}
      <div className="flex items-center justify-between p-3 bg-[#27272a] border border-[#3f3f46] rounded">
        <div className="flex gap-6 text-sm">
          <div>
            <span className="text-zinc-500">Total Shifts:</span>{' '}
            <span className="text-white font-mono">{weeklyTotals.totalShifts}</span>
          </div>
          <div>
            <span className="text-zinc-500">Total Hours:</span>{' '}
            <span className="text-white font-mono">{weeklyTotals.totalHours.toFixed(1)}</span>
          </div>
          <div>
            <span className="text-zinc-500">Assigned:</span>{' '}
            <span className="text-emerald-400 font-mono">{weeklyTotals.assignedHours.toFixed(1)}h</span>
          </div>
          <div>
            <span className="text-zinc-500">Unassigned:</span>{' '}
            <span className="text-orange-400 font-mono">{weeklyTotals.unassignedHours.toFixed(1)}h</span>
          </div>
        </div>
      </div>

      {/* Days Grid - Security Format */}
      <div className="grid grid-cols-7 gap-2" data-testid="security-format-grid">
        {days.map(([dateKey, day]) => {
          // Calculate day stats
          const shifts = day.shifts || [];
          const dayHours = shifts.reduce((sum, s) => 
            sum + calculateHours(s.start_time, s.end_time, s.break_minutes || 0), 0
          );
          const filledCount = shifts.filter(s => s.officer_id).length;
          const unfilledCount = shifts.length - filledCount;
          const acceptedCount = shifts.filter(s => s.acceptance_status === 'accepted').length;
          const pendingCount = shifts.filter(s => s.acceptance_status === 'pending_acceptance').length;
          const declinedCount = shifts.filter(s => s.acceptance_status === 'declined').length;

          return (
            <Card 
              key={dateKey} 
              className="bg-[#1a1a1d] border-[#3f3f46] overflow-hidden"
              data-testid={`day-column-${dateKey}`}
            >
              {/* Day Header - Like legacy format */}
              <CardHeader className="p-2 bg-[#27272a] border-b border-[#3f3f46]">
                <div className="text-center">
                  <div className="text-sm font-semibold text-white">
                    {formatDateKey(day.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  {/* Status indicators like (0|0|2|0) */}
                  <div className="flex items-center justify-center gap-1 mt-1 text-xs">
                    <span className="text-zinc-400">{shifts.length} shifts</span>
                    <span className="text-zinc-600">(</span>
                    <span style={{ color: STATUS_COLORS.unfilled }}>{unfilledCount}</span>
                    <span className="text-zinc-600">|</span>
                    <span style={{ color: STATUS_COLORS.pending }}>{pendingCount}</span>
                    <span className="text-zinc-600">|</span>
                    <span style={{ color: STATUS_COLORS.accepted }}>{acceptedCount}</span>
                    <span className="text-zinc-600">|</span>
                    <span style={{ color: STATUS_COLORS.declined }}>{declinedCount}</span>
                    <span className="text-zinc-600">)</span>
                  </div>
                  <div className="text-xs text-emerald-400 mt-1">
                    {dayHours.toFixed(1)} Assigned Hours
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                {/* Clone Today Button */}
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7"
                    onClick={() => handleCloneDay(day, dateKey)}
                    data-testid={`clone-today-${dateKey}`}
                  >
                    Clone Today
                  </Button>
                )}

                {/* Action Buttons Row */}
                {isAdmin && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full rounded-none bg-blue-500 hover:bg-blue-600 text-white text-xs h-8"
                      onClick={() => onAddShift && onAddShift(day, dateKey)}
                      data-testid={`assign-btn-${dateKey}`}
                    >
                      <UserPlus className="w-3 h-3 mr-1" />
                      Assign
                    </Button>
                    <div className="grid grid-cols-2 gap-px bg-[#3f3f46]">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-none bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs h-6"
                        onClick={() => {
                          // Split shift needs an existing shift - show message if no shifts
                          if (shifts.length === 0) {
                            toast.info('No shifts to split on this day');
                          } else {
                            // Show first shift to split (user can select from shift cards)
                            openSplitShift(shifts[0]);
                          }
                        }}
                        data-testid={`split-shift-${dateKey}`}
                      >
                        <Scissors className="w-3 h-3 mr-1" />
                        Split Shift
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-none bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs h-6"
                        onClick={() => openBulkShift(day, dateKey)}
                        data-testid={`bulk-shift-${dateKey}`}
                      >
                        <Layers className="w-3 h-3 mr-1" />
                        Bulk Shift
                      </Button>
                    </div>
                  </>
                )}

                {/* Shift Cards */}
                <ScrollArea className="h-[300px]">
                  <div className="p-1 space-y-1">
                    {shifts.length === 0 ? (
                      <div className="text-center py-8 text-zinc-600 text-xs">
                        No shifts
                      </div>
                    ) : (
                      shifts.map((shift) => {
                        const hours = calculateHours(shift.start_time, shift.end_time, shift.break_minutes || 0);
                        const cardColor = CARD_COLORS[shift.acceptance_status] || CARD_COLORS.scheduled;
                        
                        return (
                          <div 
                            key={shift.id}
                            className={`${cardColor} p-2 rounded text-white text-xs`}
                            data-testid={`shift-card-${shift.id}`}
                          >
                            {/* Guard Info */}
                            <div className="font-semibold truncate">
                              {shift.officer_code && `(${shift.officer_code}) `}
                              {shift.officer_name || (
                                <span className="text-orange-200 italic">Unassigned</span>
                              )}
                            </div>
                            
                            {/* Time Range + Hours */}
                            <div className="flex items-center justify-between mt-1">
                              <span>
                                {formatTimeLocal(shift.start_time)} to {formatTimeLocal(shift.end_time)}
                              </span>
                              <span className="font-mono">
                                ({formatHoursDecimal(hours)})
                              </span>
                            </div>

                            {/* Action Icons */}
                            {isAdmin && (
                              <div className="flex justify-end gap-1 mt-2">
                                <button
                                  className="p-1 bg-orange-500 hover:bg-orange-600 rounded"
                                  onClick={() => onEditShift && onEditShift(shift)}
                                  title="Edit"
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button
                                  className="p-1 bg-red-500 hover:bg-red-600 rounded"
                                  onClick={() => onDeleteShift && onDeleteShift(shift.id, shift.is_locked)}
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Footer Actions - Like legacy */}
      {isAdmin && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => toast.info('Commit This Week\'s Roster')}
            >
              Commit This Week's Roster
            </Button>
            <Button
              variant="outline"
              className="w-full border-emerald-600 text-emerald-400 hover:bg-emerald-500/10"
            >
              Commit By Day
            </Button>
            <Button
              variant="outline"
              className="w-full border-emerald-600 text-emerald-400 hover:bg-emerald-500/10"
            >
              Commit All Future Weeks For This Location
            </Button>
          </div>
          <div className="space-y-2">
            <Button
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              onClick={() => toast.info('Clean Up Roster')}
            >
              Clean Up Roster
            </Button>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => toast.info('Contract Unallocated Shifts')}
            >
              Contract Unallocated Shifts
            </Button>
          </div>
        </div>
      )}

      {/* Clone Day Dialog */}
      <Dialog open={showCloneDayDialog} onOpenChange={setShowCloneDayDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Clone Day</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Clone shifts from {sourceDay?.dateKey} to selected days
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Clone Mode */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Clone Mode</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="shifts_only"
                    checked={cloneMode === 'shifts_only'}
                    onChange={(e) => setCloneMode(e.target.value)}
                    className="accent-blue-500"
                  />
                  <span className="text-sm text-zinc-300">Shifts Only (Unassigned)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="with_assignments"
                    checked={cloneMode === 'with_assignments'}
                    onChange={(e) => setCloneMode(e.target.value)}
                    className="accent-blue-500"
                  />
                  <span className="text-sm text-zinc-300">With Assignments</span>
                </label>
              </div>
            </div>

            {/* Target Day Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Target Days</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(targetDays).map(([dateKey, selected]) => {
                  const dayInfo = days.find(([k]) => k === dateKey);
                  if (!dayInfo) return null;
                  const [_, day] = dayInfo;
                  
                  return (
                    <label 
                      key={dateKey}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                        selected ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => 
                          setTargetDays(prev => ({ ...prev, [dateKey]: checked }))
                        }
                      />
                      <span className="text-xs text-zinc-300">
                        {formatDateKey(day.date, { weekday: 'short', day: 'numeric' })}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCloneDayDialog(false)}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={executeCloneDay}
              className="bg-emerald-500 hover:bg-emerald-600"
              disabled={cloneLoading}
            >
              {cloneLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              Clone Day
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Shift Dialog */}
      <Dialog open={showBulkShiftDialog} onOpenChange={setShowBulkShiftDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Create Shifts</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Create the same shift on multiple days
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Time Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Start Time (24h)</Label>
                <input
                  type="text"
                  value={bulkShiftData.start_time}
                  onChange={(e) => setBulkShiftData(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full h-10 px-3 bg-[#09090b] border border-[#3f3f46] rounded-md text-white font-mono"
                  placeholder="06:00"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">End Time (24h)</Label>
                <input
                  type="text"
                  value={bulkShiftData.end_time}
                  onChange={(e) => setBulkShiftData(prev => ({ ...prev, end_time: e.target.value }))}
                  className="w-full h-10 px-3 bg-[#09090b] border border-[#3f3f46] rounded-md text-white font-mono"
                  placeholder="18:00"
                />
              </div>
            </div>

            {/* Target Day Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Days</Label>
              <div className="grid grid-cols-4 gap-2">
                {days.map(([dateKey, day]) => (
                  <label 
                    key={dateKey}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                      bulkTargetDays[dateKey] ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <Checkbox
                      checked={bulkTargetDays[dateKey] || false}
                      onCheckedChange={(checked) => 
                        setBulkTargetDays(prev => ({ ...prev, [dateKey]: checked }))
                      }
                    />
                    <span className="text-xs text-zinc-300">
                      {formatDateKey(day.date, { weekday: 'short', day: 'numeric' })}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowBulkShiftDialog(false)}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={executeBulkShift}
              className="bg-blue-500 hover:bg-blue-600"
              disabled={bulkLoading}
            >
              {bulkLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Layers className="w-4 h-4 mr-2" />
              )}
              Create Shifts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Shift Dialog */}
      <Dialog open={showSplitShiftDialog} onOpenChange={setShowSplitShiftDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Split Shift</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Split this shift into two separate shifts
            </DialogDescription>
          </DialogHeader>
          
          {shiftToSplit && (
            <div className="space-y-4">
              {/* Original Shift Info */}
              <div className="p-3 bg-zinc-800 rounded">
                <div className="text-sm text-zinc-400">Original Shift</div>
                <div className="text-white font-mono">
                  {formatTimeLocal(shiftToSplit.start_time)} - {formatTimeLocal(shiftToSplit.end_time)}
                </div>
                <div className="text-xs text-zinc-500">
                  {shiftToSplit.officer_name || 'Unassigned'}
                </div>
              </div>

              {/* Split Point */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Split At (24h)</Label>
                <input
                  type="text"
                  value={splitTime}
                  onChange={(e) => setSplitTime(e.target.value)}
                  className="w-full h-10 px-3 bg-[#09090b] border border-[#3f3f46] rounded-md text-white font-mono"
                  placeholder="12:00"
                />
              </div>

              {/* Preview */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded">
                  <div className="text-xs text-emerald-400">Part 1</div>
                  <div className="text-sm text-white font-mono">
                    {formatTimeLocal(shiftToSplit.start_time)} - {splitTime}
                  </div>
                </div>
                <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded">
                  <div className="text-xs text-emerald-400">Part 2</div>
                  <div className="text-sm text-white font-mono">
                    {splitTime} - {formatTimeLocal(shiftToSplit.end_time)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSplitShiftDialog(false)}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={executeSplitShift}
              className="bg-orange-500 hover:bg-orange-600"
              disabled={splitLoading}
            >
              {splitLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Scissors className="w-4 h-4 mr-2" />
              )}
              Split Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
